import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { TypeIcon } from './AddressCard';
import {
  EMPTY_FORM,
  ADDRESS_TYPES,
  REQUIRED,
  MAX_ADDRESS_LENGTH,
  validateField,
} from './addressValidation';
import usePincodeCheck from '../../../../utils/usePincodeCheck';
import usePincodeLocation from '../../../../utils/usePincodeLocation';
import useBodyScrollLock from '../../../../utils/useBodyScrollLock';

const AddressFormModal = ({ initial = EMPTY_FORM, loading, onSubmit, onCancel, title }) => {
  useBodyScrollLock(true);

  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Use backend city/state when serviceable, with the postal API as fallback.
  const { status: pincodeStatus, message: pincodeApiMessage, data: pincodeData, checkedPincode } = usePincodeCheck(form.pincode?.trim());
  const pincodeLocation = usePincodeLocation(form.pincode?.trim());
  const locationMatchesPincode = pincodeLocation?.pincode === String(form.pincode || '').trim();
  const backendResultMatchesPincode = checkedPincode === String(form.pincode || '').trim();
  const backendCity = pincodeStatus === 'serviceable' && backendResultMatchesPincode ? (pincodeData?.city_name || pincodeData?.city || '') : '';
  const backendState = pincodeStatus === 'serviceable' && backendResultMatchesPincode ? (pincodeData?.state || pincodeData?.state_name || '') : '';
  const pincodeCity = backendCity || (locationMatchesPincode ? pincodeLocation.city : '');
  const pincodeState = backendState || (locationMatchesPincode ? pincodeLocation.state : '');

  useEffect(() => {
    setForm(initial);
    setErrors({});
    setTouched({});
  }, [initial]);

  useEffect(() => {
    if (!/^\d{6}$/.test(String(form.pincode || '').trim())) {
      setForm((prev) => prev.city || prev.state ? { ...prev, city: '', state: '' } : prev);
    }
  }, [form.pincode]);

  useEffect(() => {
    if (
      (pincodeCity && (!touched.city || !form.city?.trim())) ||
      (pincodeState && (!touched.state || !form.state?.trim()))
    ) {
      setForm((p) => ({
        ...p,
        ...((pincodeCity && (!touched.city || !p.city?.trim())) ? { city: pincodeCity } : {}),
        ...((pincodeState && (!touched.state || !p.state?.trim())) ? { state: pincodeState } : {}),
      }));
    }
  }, [pincodeCity, pincodeState, touched.city, touched.state, form.city, form.state]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let clean = value;
    if (name === 'pincode') clean = value.replace(/\D/g, '').slice(0, 6);
    else if (name === 'mobile') clean = value.replace(/\D/g, '').slice(0, 10);
    else if (name === 'city' || name === 'state') clean = value.replace(/[^a-zA-Z\s]/g, '');
    else if (name === 'address') clean = value.slice(0, MAX_ADDRESS_LENGTH);
    else if (name === 'gstin') clean = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 15);
    const final = type === 'checkbox' ? checked : clean;
    setForm((p) => name === 'pincode' && clean.length !== 6
      ? { ...p, pincode: final, city: '', state: '' }
      : { ...p, [name]: final });
    if (name === 'city' || name === 'state')
      setTouched((p) => ({ ...p, [name]: true }));
    if (touched[name]) setErrors((p) => ({ ...p, [name]: validateField(name, final) }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((p) => ({ ...p, [name]: true }));
    setErrors((p) => ({ ...p, [name]: validateField(name, value) }));
  };

  const handleUseLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm((p) => ({
          ...p,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6),
        }));
        toast.success('GPS coordinates detected!');
      },
      () => toast.error('Unable to get location. Please allow location access in your browser.'),
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (pincodeStatus === 'not_serviceable' || pincodeStatus === 'error') {
      toast.error('Cannot save address — delivery is not available for this pincode.');
      return;
    }
    if (pincodeStatus === 'checking') {
      toast('Checking pincode serviceability, please wait…', { icon: '⏳' });
      return;
    }
    const newErrors = {};
    const newTouched = {};
    REQUIRED.forEach((f) => {
      newTouched[f] = true;
      newErrors[f] = validateField(f, form[f] ?? '');
    });
    setTouched(newTouched);
    setErrors(newErrors);
    if (Object.values(newErrors).some((err) => err)) return;
    onSubmit(form);
  };

  const ic = (name) =>
    `w-full rounded-xl border px-3 py-2.5 text-xs sm:text-sm outline-none transition focus:ring-2 ${
      errors[name] && touched[name]
        ? 'border-red-300 bg-red-50/30 focus:border-red-400 focus:ring-red-100'
        : 'border-gray-200 bg-white focus:border-[#046a38] focus:ring-emerald-100'
    }`;

  const autoFilled = (name) =>
    (name === 'city' ? pincodeCity : name === 'state' ? pincodeState : '') &&
    !touched[name];

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-xs p-0 sm:p-4 font-sans">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-lg flex flex-col rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl max-h-[92dvh] overflow-hidden"
      >
        {/* Mobile drag handle indicator */}
        <div className="w-full pt-2.5 pb-1 flex justify-center sm:hidden bg-white">
          <div className="h-1.5 w-12 rounded-full bg-gray-300" />
        </div>

        {/* Modal Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-100 bg-white px-5 sm:px-6 py-3.5 sm:py-4">
          <h2 className="text-base sm:text-lg font-black text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={onCancel}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 active:scale-95 transition"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Scrollable Body */}
        <div className="flex-1 overflow-y-auto space-y-3.5 sm:space-y-4 p-5 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-bold text-gray-600">Full Name *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                onBlur={handleBlur}
                className={ic('name')}
                placeholder="e.g. Rahul Sharma"
              />
              {errors.name && touched.name && <p className="mt-1 text-[11px] text-red-500">{errors.name}</p>}
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold text-gray-600">Mobile Number *</label>
              <input
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                className={ic('mobile')}
                placeholder="10-digit mobile number"
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                maxLength={10}
              />
              {errors.mobile && touched.mobile && <p className="mt-1 text-[11px] text-red-500">{errors.mobile}</p>}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold text-gray-600">Email Address (optional)</label>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={ic('email')}
              placeholder="rahul@example.com"
            />
            {errors.email && touched.email && <p className="mt-1 text-[11px] text-red-500">{errors.email}</p>}
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold text-gray-600">GSTIN (optional)</label>
            <input
              name="gstin"
              value={form.gstin || ''}
              onChange={handleChange}
              maxLength={15}
              autoCapitalize="characters"
              className={ic('gstin')}
              placeholder="Enter GSTIN for a GST invoice"
            />
          </div>

          {/* Address Type Selector */}
          <div>
            <label className="mb-1.5 block text-xs font-bold text-gray-600">Address Type</label>
            <div className="flex gap-2">
              {ADDRESS_TYPES.map((t) => {
                const isSelected = form.address_type === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, address_type: t }))}
                    className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border py-2.5 text-xs font-bold transition active:scale-95 ${
                      isSelected
                        ? 'border-[#046a38] bg-emerald-50 text-[#046a38] shadow-xs'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <TypeIcon type={t} />
                    <span>{t}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Street Address */}
          <div>
            <label className="mb-1 block text-xs font-bold text-gray-600">House No., Building, Street Address *</label>
            <textarea
              name="address"
              value={form.address}
              onChange={handleChange}
              onBlur={handleBlur}
              rows={2}
              maxLength={MAX_ADDRESS_LENGTH}
              className={ic('address')}
              placeholder="Flat no., Apartment, Street, Landmark"
            />
            <div className="flex justify-between items-center mt-1">
              {errors.address && touched.address ? (
                <p className="text-[11px] text-red-500">{errors.address}</p>
              ) : <span />}
              <p className="text-[10px] text-gray-400">{form.address.length}/{MAX_ADDRESS_LENGTH}</p>
            </div>
          </div>

          {/* Pincode, City, State */}
          <div className="grid grid-cols-3 gap-2.5 sm:gap-3">
            <div>
              <label className="mb-1 block text-xs font-bold text-gray-600">Pincode *</label>
              <input
                name="pincode"
                value={form.pincode}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={6}
                inputMode="numeric"
                placeholder="6 digits"
                className={`${ic('pincode')} ${
                  pincodeStatus === 'serviceable' ? '!border-emerald-500 !bg-emerald-50/30' :
                  pincodeStatus === 'not_serviceable' ? '!border-red-300 !bg-red-50/30' : ''
                }`}
              />
              {errors.pincode && touched.pincode && (
                <p className="mt-1 text-[10px] text-red-500">{errors.pincode}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold text-gray-600">City *</label>
              <input
                name="city"
                value={form.city}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`${ic('city')} ${autoFilled('city') ? 'bg-emerald-50/20' : ''}`}
                placeholder="Pune"
              />
              {errors.city && touched.city && (
                <p className="mt-1 text-[10px] text-red-500">{errors.city}</p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold text-gray-600">State *</label>
              <input
                name="state"
                value={form.state}
                onChange={handleChange}
                onBlur={handleBlur}
                className={`${ic('state')} ${autoFilled('state') ? 'bg-emerald-50/20' : ''}`}
                placeholder="Maharashtra"
              />
              {errors.state && touched.state && (
                <p className="mt-1 text-[10px] text-red-500">{errors.state}</p>
              )}
            </div>
          </div>

          {/* Serviceability Banner */}
          {form.pincode?.length === 6 && pincodeStatus !== 'idle' && (
            <div className={`flex items-center gap-2 rounded-xl px-3.5 py-2.5 text-xs font-semibold ${
              pincodeStatus === 'checking'
                ? 'bg-gray-50 text-gray-600 border border-gray-200'
                : pincodeStatus === 'serviceable'
                  ? 'bg-emerald-50 text-[#046a38] border border-emerald-200'
                  : 'bg-amber-50 text-amber-800 border border-amber-200'
            }`}>
              {pincodeStatus === 'checking' ? (
                <>
                  <svg className="h-3.5 w-3.5 animate-spin text-[#046a38]" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Checking delivery serviceability…</span>
                </>
              ) : (
                <>
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-white text-[10px] font-bold ${
                    pincodeStatus === 'serviceable' ? 'bg-[#046a38]' : 'bg-amber-500'
                  }`}>
                    {pincodeStatus === 'serviceable' ? '✓' : '!'}
                  </span>
                  <span>
                    {pincodeStatus === 'serviceable'
                      ? `Delivery Available — ${pincodeData?.area_name ? `${pincodeData.area_name}, ` : ''}${pincodeData?.city_name || 'Your area'} 🎉`
                      : pincodeApiMessage || 'Delivery not yet available in this pincode. We are expanding soon!'}
                  </span>
                </>
              )}
            </div>
          )}

          {/* GPS Coordinates Section */}
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-xs font-bold text-gray-500">
                GPS Location <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <button
                type="button"
                onClick={handleUseLocation}
                className="flex items-center gap-1 rounded-lg bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-[#046a38] hover:bg-emerald-100 transition active:scale-95"
              >
                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>Detect My Location</span>
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <input
                  name="latitude"
                  type="number"
                  step="any"
                  value={form.latitude}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={ic('latitude')}
                  placeholder="Latitude (18.52)"
                />
              </div>
              <div>
                <input
                  name="longitude"
                  type="number"
                  step="any"
                  value={form.longitude}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={ic('longitude')}
                  placeholder="Longitude (73.85)"
                />
              </div>
            </div>
          </div>

          {/* Set as default address toggle */}
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-100 bg-gray-50/80 p-3.5 transition hover:bg-emerald-50/30">
            <input
              name="is_default"
              type="checkbox"
              checked={form.is_default}
              onChange={handleChange}
              className="h-4 w-4 rounded border-gray-300 text-[#046a38] focus:ring-[#046a38]"
            />
            <div>
              <p className="text-xs sm:text-sm font-bold text-gray-900">Set as default delivery address</p>
              <p className="text-[11px] text-gray-500">Auto-selected during quick checkout</p>
            </div>
          </label>
        </div>

        {/* Modal Sticky Bottom Actions */}
        <div className="sticky bottom-0 flex gap-2.5 border-t border-gray-100 bg-white p-4 sm:p-5">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-xl border border-gray-200 py-3 text-xs sm:text-sm font-bold text-gray-700 hover:bg-gray-50 active:scale-95 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={
              loading ||
              pincodeStatus === 'not_serviceable' ||
              pincodeStatus === 'checking' ||
              pincodeStatus === 'error'
            }
            className="flex-[2] rounded-xl bg-[#046a38] py-3 text-xs sm:text-sm font-bold text-white shadow-md shadow-[#046a38]/20 hover:bg-[#03522b] active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 transition"
          >
            {loading
              ? 'Saving…'
              : pincodeStatus === 'checking'
                ? 'Verifying pincode…'
                : 'Save Address'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddressFormModal;
